import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Loader2, Play } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@orisini/ui/components/card'
import { Input } from '@orisini/ui/components/input'
import { Label } from '@orisini/ui/components/label'
import { api } from '@orisini/ui/lib/api'

type TtsProvider = 'workers-ai' | 'elevenlabs' | 'gemini'
type SttProvider = 'workers-ai' | 'gemini'

interface VoiceSettings {
  tts: {
    provider: TtsProvider
    workersAi: { model: string; speaker: string }
    elevenlabs: { voiceId: string; modelId: string }
    gemini: { model: string; voice: string }
  }
  stt: { provider: SttProvider; workersAi: { model: string }; gemini: { model: string } }
}

const TTS: Record<TtsProvider, { name: string; hint: string }> = {
  'workers-ai': { name: 'Cloudflare Workers AI', hint: 'No API key — billed to the Cloudflare account. Deepgram Aura voices are English only.' },
  elevenlabs: { name: 'ElevenLabs', hint: 'Best quality and many languages. Needs the ElevenLabs key on the Content row below.' },
  gemini: { name: 'Google Gemini', hint: 'Many languages, including Indonesian. Uses the Gemini key on the Content row below.' },
}

const STT: Record<SttProvider, { name: string; hint: string }> = {
  'workers-ai': { name: 'Cloudflare Workers AI (Whisper)', hint: 'No API key. Accurate timings, which keep scenes in step with the voice.' },
  gemini: { name: 'Google Gemini', hint: 'Uses the Gemini key on the Content row. Timings are rougher.' },
}

/**
 * Admin → AI → Voice & transcription: penyedia suara video Studio dan
 * transkripsi voice-over unggahan. Kuncinya ada di baris "Content" di bawah;
 * di sini hanya pilihan penyedia, model, dan suara.
 */
export function VoicePanel() {
  const [v, setV] = useState<VoiceSettings | null>(null)
  const [saved, setSaved] = useState('')
  const [busy, setBusy] = useState<'save' | 'test' | null>(null)
  const audio = useRef<HTMLAudioElement>(null)
  const [src, setSrc] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ voice: VoiceSettings }>('/admin/ai/voice')
      .then((r) => {
        setV(r.voice)
        setSaved(JSON.stringify(r.voice))
      })
      .catch(() => toast.error("Couldn't load the voice settings"))
  }, [])
  useEffect(() => () => void (src && URL.revokeObjectURL(src)), [src])

  if (!v) return <Loader2 className="size-4 animate-spin text-muted-foreground" />
  const dirty = JSON.stringify(v) !== saved
  const set = (patch: (x: VoiceSettings) => VoiceSettings) => setV(patch(structuredClone(v)))

  const simpan = async () => {
    setBusy('save')
    try {
      const r = await api.put<{ voice: VoiceSettings }>('/admin/ai/voice', v)
      setV(r.voice)
      setSaved(JSON.stringify(r.voice))
      toast.success('Voice settings saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't save")
    } finally {
      setBusy(null)
    }
  }

  const coba = async () => {
    if (dirty && !(await simpan().then(() => true))) return
    setBusy('test')
    try {
      const res = await fetch('/api/admin/ai/voice/test', { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: '{}' })
      if (!res.ok) {
        const b = (await res.json().catch(() => ({}))) as { error?: { message?: string } }
        throw new Error(b.error?.message ?? `The voice provider answered ${res.status}`)
      }
      const url = URL.createObjectURL(await res.blob())
      setSrc(url)
      setTimeout(() => void audio.current?.play().catch(() => undefined), 50)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't make a sample")
    } finally {
      setBusy(null)
    }
  }

  const t = v.tts
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Voice &amp; transcription</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5 text-sm">
        <div className="space-y-2">
          <p className="font-medium">Voice for videos</p>
          {(Object.keys(TTS) as TtsProvider[]).map((p) => (
            <label key={p} className="flex items-start gap-2">
              <input type="radio" name="tts" className="mt-1" checked={t.provider === p} onChange={() => set((x) => ((x.tts.provider = p), x))} />
              <span>
                <span className="font-medium">{TTS[p].name}</span>
                <span className="block text-xs text-muted-foreground">{TTS[p].hint}</span>
              </span>
            </label>
          ))}
          <div className="grid gap-3 pt-1 sm:grid-cols-2">
            {t.provider === 'workers-ai' && (
              <>
                <Field label="Model" value={t.workersAi.model} onChange={(s) => set((x) => ((x.tts.workersAi.model = s), x))} />
                <Field label="Speaker" value={t.workersAi.speaker} onChange={(s) => set((x) => ((x.tts.workersAi.speaker = s), x))} hint="e.g. luna, orion, athena" />
              </>
            )}
            {t.provider === 'elevenlabs' && (
              <>
                <Field label="Voice ID" value={t.elevenlabs.voiceId} onChange={(s) => set((x) => ((x.tts.elevenlabs.voiceId = s), x))} hint="From the ElevenLabs voice library" />
                <Field label="Model" value={t.elevenlabs.modelId} onChange={(s) => set((x) => ((x.tts.elevenlabs.modelId = s), x))} />
              </>
            )}
            {t.provider === 'gemini' && (
              <>
                <Field label="Model" value={t.gemini.model} onChange={(s) => set((x) => ((x.tts.gemini.model = s), x))} />
                <Field label="Voice" value={t.gemini.voice} onChange={(s) => set((x) => ((x.tts.gemini.voice = s), x))} hint="e.g. Kore, Puck, Charon" />
              </>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <p className="font-medium">Transcribing uploaded voice-overs</p>
          {(Object.keys(STT) as SttProvider[]).map((p) => (
            <label key={p} className="flex items-start gap-2">
              <input type="radio" name="stt" className="mt-1" checked={v.stt.provider === p} onChange={() => set((x) => ((x.stt.provider = p), x))} />
              <span>
                <span className="font-medium">{STT[p].name}</span>
                <span className="block text-xs text-muted-foreground">{STT[p].hint}</span>
              </span>
            </label>
          ))}
          <div className="grid gap-3 pt-1 sm:grid-cols-2">
            {v.stt.provider === 'workers-ai' ? (
              <Field label="Model" value={v.stt.workersAi.model} onChange={(s) => set((x) => ((x.stt.workersAi.model = s), x))} />
            ) : (
              <Field label="Model" value={v.stt.gemini.model} onChange={(s) => set((x) => ((x.stt.gemini.model = s), x))} />
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground">Model names expire. If voices suddenly fail with a "not found" error, check the provider's model list before the key.</p>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={simpan} disabled={!dirty || busy !== null}>
            {busy === 'save' && <Loader2 className="animate-spin" />} Save
          </Button>
          <Button variant="outline" onClick={coba} disabled={busy !== null}>
            {busy === 'test' ? <Loader2 className="animate-spin" /> : <Play />} Play a sample
          </Button>
          {src && <audio ref={audio} controls src={src} className="h-8" />}
        </div>
      </CardContent>
    </Card>
  )
}

function Field({ label, value, hint, onChange }: { label: string; value: string; hint?: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input className="h-8" value={value} onChange={(e) => onChange(e.target.value)} />
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}
